import React, { useState, useEffect } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Dimensions, Modal, Pressable, TextInput } from "react-native";
import { getFirestore, doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { useUser } from "../../Widgets/UserContext";
import RSVPDataService from "../../services/RSVPDataService";
import reminderIntegration from "../../services/ReminderIntegration";

const ADMIN_EMAIL_WHITELIST = [
    'trevorhawkins@dsignable.com',
    'info@mytassel.com',
];
import { handleAutoResubscribe } from "../../services/AutoResubscribeService";

const fontFamilies = {
    Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width < 1440;
const isLargeDesktop = width >= 1440;

const RSVP = ({ pageOwnerId, widgetIndex }) => {
    const db = getFirestore();
    const { currentUser, currentUserData } = useUser();
    const [rsvp, setRSVP] = useState("");
    const [counts, setCounts] = useState({ Going: 0, Maybe: 0, Decline: 0 });
    const [showReminderSettings, setShowReminderSettings] = useState(false);
    const [reminderSettings, setReminderSettings] = useState({ enabled: false, reminderTimes: [] });
    const [isOwner, setIsOwner] = useState(false);
    
    // Feature flag for reminders
    const [showRemindersFeature, setShowRemindersFeature] = useState(true);

    // Dynamic reminder options from Firestore
    const fallbackReminderOptions = [
        { value: 'days_before_10', label: 'Day of Event + 10 Days Before' },
        { value: 'days_before_7', label: 'Day of Event + 7 Days Before' },
        { value: 'days_before_3', label: 'Day of Event + 3 Days Before' },
        { value: 'days_before_1', label: 'Day of Event + 1 Day Before' }
    ];
    const [reminderOptions, setReminderOptions] = useState(fallbackReminderOptions);
    
    // Event details states
    const [showEventDetails, setShowEventDetails] = useState(false);
    const [eventDetails, setEventDetails] = useState({
        title: '',
        date: '',
        location: '',
        description: ''
    });
    const [hasEventDetails, setHasEventDetails] = useState(false);

    useEffect(() => {
        loadRSVPData();
        const ownerStatus = currentUser?.uid === pageOwnerId;
        setIsOwner(ownerStatus);
        
        
        
        
        
    }, [currentUser, pageOwnerId]);

    // Fetch feature flags - with environment-specific support
    useEffect(() => {
        const fetchFeatureFlags = async () => {
            try {
                // Determine current environment
                const isProduction = typeof window !== 'undefined' && window.location?.hostname === 'mytassel.com';
                const env = isProduction ? 'production' : 'staging';
                
                const configRef = doc(db, 'config', 'features');
                const configSnap = await getDoc(configRef);
                if (configSnap.exists()) {
                    const flags = configSnap.data();
                    const flag = flags.showRemindersSystem;
                    
                    // Handle both legacy boolean and new { staging, production } format
                    const flagValue = typeof flag === 'boolean' ? flag : flag?.[env] ?? true;
                    setShowRemindersFeature(flagValue);
                }
            } catch (error) {
                
            }
        };
        fetchFeatureFlags();
    }, [db]);

    useEffect(() => {
        const fetchReminderOptions = async () => {
            try {
                const configRef = doc(db, 'config', 'reminderSettings');
                const snap = await getDoc(configRef);
                if (snap.exists() && snap.data().defaultOptions?.length) {
                    setReminderOptions(snap.data().defaultOptions.map(opt => ({
                        value: opt.value,
                        label: opt.label,
                    })));
                }
            } catch (err) {
                console.warn('Using fallback reminder options:', err.message);
            }
        };
        fetchReminderOptions();
    }, [db]);

    const loadRSVPData = async () => {
        if (!pageOwnerId) return;
        const docRef = doc(db, "users", pageOwnerId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            
            // Use RSVPDataService for consistent data extraction
            const rsvpData = RSVPDataService.extractRsvpDataFromUserData(data, data.pages?.eventsPageConfig);
            
            // Convert RSVP data arrays to counts
            const rsvpCounts = {
                Going: rsvpData.Going?.length || 0,
                Maybe: rsvpData.Maybe?.length || 0,
                Decline: rsvpData.Decline?.length || 0,
            };
            
            setCounts(rsvpCounts);
            
            const reminderData = data.pages?.eventsPageConfig?.reminderSettings || { enabled: false, reminderTimes: [] };
            setReminderSettings(reminderData);
            
            // Load event details from the RSVP card
            const eventsCards = data.pages?.eventsPageConfig?.cards || [];
            const rsvpCard = eventsCards.find(card => card.type === 'RSVP' && card.position === (widgetIndex + 1));
            if (rsvpCard) {
                const details = {
                    title: rsvpCard.eventTitle || '',
                    date: rsvpCard.eventDate || '',
                    location: rsvpCard.eventLocation || '',
                    description: rsvpCard.eventDescription || ''
                };
                setEventDetails(details);
                setHasEventDetails(!!(details.title || details.date || details.location || details.description));
            }
            
            // Check user's current RSVP status if logged in
            if (currentUser) {
                let userStatus = "";
                
                // Check extracted RSVP data for user's status
                if (rsvpData.Going?.some(entry => 
                    (typeof entry === 'string' && entry === currentUser.uid) ||
                    (typeof entry === 'object' && entry.email === currentUser.email)
                )) {
                    userStatus = "Going";
                } else if (rsvpData.Maybe?.some(entry => 
                    (typeof entry === 'string' && entry === currentUser.uid) ||
                    (typeof entry === 'object' && entry.email === currentUser.email)
                )) {
                    userStatus = "Maybe";
                } else if (rsvpData.Decline?.some(entry => 
                    (typeof entry === 'string' && entry === currentUser.uid) ||
                    (typeof entry === 'object' && entry.email === currentUser.email)
                )) {
                    userStatus = "Decline";
                }
                
                setRSVP(userStatus);
            } else {
                // Clear RSVP state when user is logged out
                setRSVP("");
            }
        }
    };

    const handleRSVP = async (option) => {
        if (!currentUser) {
            // For anonymous users, prompt for email to store RSVP
            const email = prompt("Please enter your email to RSVP:");
            if (!email) return;
            
            try {
                const userDocRef = doc(db, "users", pageOwnerId);
                const docSnap = await getDoc(userDocRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const cards = data.pages?.eventsPageConfig?.cards || [];
                    
                    // Find and update RSVP widget in cards array
                    let widgetFound = false;
                    const updatedCards = cards.map(card => {
                        if (card.type === 'RSVP') {
                            widgetFound = true;
                            
                            // Remove email from all RSVP lists first
                            ['going', 'maybe', 'decline'].forEach(status => {
                                card[status] = (card[status] || []).filter(entry => 
                                    (typeof entry === 'string' && entry !== email) ||
                                    (typeof entry === 'object' && entry.email !== email)
                                );
                            });
                            
                            // Add email to selected option
                            const targetStatus = option.toLowerCase();
                            if (!card[targetStatus]) card[targetStatus] = [];
                            card[targetStatus].push({ 
                                email, 
                                firstName: '', 
                                lastName: '',
                                timestamp: new Date().toISOString()
                            });
                            
                            return card;
                        }
                        return card;
                    });
                    
                    if (widgetFound) {
                        // Update Firestore with new cards array
                        await updateDoc(userDocRef, {
                            "pages.eventsPageConfig.cards": updatedCards,
                        });
                    } else {
                        // Fallback to legacy RSVP format
                        const rsvpData = data.pages?.eventsPageConfig?.rsvp || { Going: [], Maybe: [], Decline: [] };
                        
                        // Remove email from all lists
                        rsvpData.Going = rsvpData.Going.filter(entry => 
                            typeof entry === 'string' ? entry !== email : entry.email !== email
                        );
                        rsvpData.Maybe = rsvpData.Maybe.filter(entry => 
                            typeof entry === 'string' ? entry !== email : entry.email !== email
                        );
                        rsvpData.Decline = rsvpData.Decline.filter(entry => 
                            typeof entry === 'string' ? entry !== email : entry.email !== email
                        );
                        
                        // Add to selected option
                        rsvpData[option].push({ email, isAnonymous: true, timestamp: Date.now() });
                        
                        await updateDoc(userDocRef, {
                            "pages.eventsPageConfig.rsvp": rsvpData,
                        });
                    }
                    
                    // Update local state with anonymous user's selection
                    setRSVP(option);
                    
                    // Auto-subscribe anonymous user to reminder emails if they RSVP Going/Maybe
                    if (email && (option === 'Going' || option === 'Maybe')) {
                        try {
                            const subscribeResult = await handleAutoResubscribe(
                                email,
                                pageOwnerId,
                                option,
                                true // isClient = true
                            );
                            if (subscribeResult.success) {
                                console.log('✅ Anonymous user auto-subscribed to reminder emails via RSVP');
                            }
                        } catch (error) {
                            console.error('⚠️ Failed to auto-subscribe anonymous user:', error);
                            // Don't show error to user - this is a background operation
                        }
                    }
                    
                    // Reload the counts with a small delay to ensure Firestore has updated
                    setTimeout(() => loadRSVPData(), 200);
                    
                    alert(`Thank you! Your RSVP (${option}) has been recorded.`);
                }
            } catch (error) {
                
                alert('Failed to save RSVP. Please try again.');
            }
            return;
        }
        
        const userDocRef = doc(db, "users", pageOwnerId);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            const cards = data.pages?.eventsPageConfig?.cards || [];
            
            // Find the RSVP card
            let rsvpCardIndex = cards.findIndex(card => card.type === 'RSVP');
            if (rsvpCardIndex === -1) {
                // Create RSVP card if it doesn't exist
                cards.push({
                    type: 'RSVP',
                    going: [],
                    maybe: [],
                    decline: [],
                    id: Date.now().toString(),
                    position: 0
                });
                rsvpCardIndex = 0;
            }
            
            const rsvpCard = cards[rsvpCardIndex];
            
            // Remove user from all RSVP lists (check both UID and email)
            ['going', 'maybe', 'decline'].forEach(status => {
                rsvpCard[status] = (rsvpCard[status] || []).filter(entry => 
                    (typeof entry === 'string' && entry !== currentUser.uid) ||
                    (typeof entry === 'object' && entry.email !== currentUser.email)
                );
            });
            
            // Add user to selected RSVP list
            const targetStatus = option.toLowerCase();
            if (!rsvpCard[targetStatus]) rsvpCard[targetStatus] = [];
            rsvpCard[targetStatus].push(currentUser.uid);
            
            // Update Firestore with the modified cards array
            await updateDoc(userDocRef, {
                "pages.eventsPageConfig.cards": cards
            });
            
            // Update local state
            setRSVP(option);
            
            // Auto-resubscribe user to reminder emails if they RSVP Going/Maybe
            if (currentUser?.email && (option === 'Going' || option === 'Maybe')) {
                try {
                    const resubscribeResult = await handleAutoResubscribe(
                        currentUser.email,
                        pageOwnerId,
                        option,
                        true // isClient = true
                    );
                    if (resubscribeResult.success) {
                        console.log('✅ User auto-resubscribed to reminder emails via RSVP');
                    }
                } catch (error) {
                    console.error('⚠️ Failed to auto-resubscribe user:', error);
                    // Don't show error to user - this is a background operation
                }
            }
            
            // Update counts manually for immediate feedback
            const newCounts = {
                Going: (rsvpCard.going || []).length,
                Maybe: (rsvpCard.maybe || []).length,
                Decline: (rsvpCard.decline || []).length,
            };
            setCounts(newCounts);
        }
    };

    const handleReminderToggle = (reminderTime) => {
        const currentTimes = reminderSettings.reminderTimes || [];
        const newReminderTimes = currentTimes.includes(reminderTime)
            ? currentTimes.filter(t => t !== reminderTime && t !== 'day_of')
            : [...currentTimes.filter(t => !t.startsWith('days_before_')), 'day_of', reminderTime];
        
        setReminderSettings(prev => ({
            ...prev,
            reminderTimes: newReminderTimes,
            enabled: newReminderTimes.length > 0
        }));
    };

    const saveReminderSettings = async () => {
        try {
            // Use direct Firestore approach like EventManagement component
            const userDocRef = doc(db, "users", pageOwnerId);
            
            // Use setDoc with merge to create document if it doesn't exist
            await setDoc(userDocRef, {
                pages: {
                    eventsPageConfig: {
                        reminderSettings: {
                            enabled: reminderSettings.reminderTimes.length > 0,
                            reminderTimes: reminderSettings.reminderTimes,
                            lastUpdated: new Date().toISOString()
                        }
                    }
                }
            }, { merge: true });
            
            setShowReminderSettings(false);
            alert("Reminder settings saved successfully!");
        } catch (error) {
            
            alert(`Failed to save reminder settings: ${error.message || 'Please try again.'}`);
        }
    };

    const scheduleTestReminder = async (testDelay) => {
        try {
            const result = await reminderIntegration.callAPI('/schedule-event-reminders', {
                eventOwnerId: pageOwnerId,
                testMode: true,
                testDelay,
            });
            if (result?.success) {
                alert(`Test reminder scheduled! It will fire in ${testDelay} minutes.`);
            } else {
                alert(`Test reminder failed: ${result?.reason || result?.error || 'Unknown error'}`);
            }
        } catch (error) {
            alert(`Failed to schedule test reminder: ${error.message}`);
        }
    };

    const saveEventDetails = async () => {
        try {
            const userDocRef = doc(db, "users", pageOwnerId);
            const docSnap = await getDoc(userDocRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                const cards = data.pages?.eventsPageConfig?.cards || [];
                
                // Find and update the RSVP card
                const updatedCards = cards.map(card => {
                    if (card.type === 'RSVP' && card.position === (widgetIndex + 1)) {
                        return {
                            ...card,
                            eventTitle: eventDetails.title,
                            eventDate: eventDetails.date,
                            eventLocation: eventDetails.location,
                            eventDescription: eventDetails.description
                        };
                    }
                    return card;
                });

                // If no RSVP card found, create one
                if (!updatedCards.find(card => card.type === 'RSVP' && card.position === (widgetIndex + 1))) {
                    updatedCards.push({
                        id: Date.now().toString(),
                        type: 'RSVP',
                        position: widgetIndex + 1,
                        going: [],
                        maybe: [],
                        decline: [],
                        eventTitle: eventDetails.title,
                        eventDate: eventDetails.date,
                        eventLocation: eventDetails.location,
                        eventDescription: eventDetails.description
                    });
                }

                await updateDoc(userDocRef, {
                    "pages.eventsPageConfig.cards": updatedCards
                });
                
                // AUTOMATICALLY RESCHEDULE REMINDERS when event details change
                try {
                    
                    const rescheduleResult = await reminderIntegration.updateEventReminders(pageOwnerId, {
                        title: eventDetails.title,
                        date: eventDetails.date,
                        location: eventDetails.location,
                        description: eventDetails.description
                    });
                    
                    if (rescheduleResult.success) {
                        console.log('✅ Reminders automatically rescheduled:', {
                            canceled: rescheduleResult.canceled,
                            rescheduled: rescheduleResult.rescheduled,
                            recipients: rescheduleResult.recipientCount
                        });
                    } else {
                        
                    }
                } catch (reminderError) {
                    
                    // Don't fail the event save if reminder rescheduling fails
                }
                
                setHasEventDetails(!!(eventDetails.title || eventDetails.date || eventDetails.location || eventDetails.description));
                setShowEventDetails(false);
                alert("Event details saved successfully!");
            }
        } catch (error) {
            
            alert(`Failed to save event details: ${error.message || 'Please try again.'}`);
        }
    };

    const isAdminUser = currentUserData?.accountType === 'admin' ||
        ADMIN_EMAIL_WHITELIST.includes(currentUserData?.email?.toLowerCase());

    const testReminderOptions = [
        { value: 'test_5min', label: 'TEST: Send in 5 minutes', testDelay: 5 },
        { value: 'test_10min', label: 'TEST: Send in 10 minutes', testDelay: 10 },
    ];

    const styles = StyleSheet.create({
        container: {
            flexDirection: "row",
            justifyContent: "space-around",
            paddingHorizontal: isMobile ? 5 : "auto", // Adjust padding for mobile
        },
        button: {
            padding: isMobile ? 10 : 30, // Adjust padding for mobile
            borderRadius: 15,
            backgroundColor: "#ccc",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
            position: "relative",
            marginHorizontal: isMobile ? 5 : 10, // Add margin for mobile
        },
        selectedButton: {
            padding: isMobile ? 10 : 30, // Adjust padding for mobile
            borderRadius: 15,
            backgroundColor: "grey",
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 3.84,
            elevation: 5,
            position: "relative",
            marginHorizontal: isMobile ? 5 : 10, // Add margin for mobile
        },
        text: {
            fontFamily: fontFamilies.Bodoni72Smallcaps,
            fontWeight: "normal",
            fontSize: isMobile ? 16 : 22,
            textAlign: "left",
            color: "#fff",
            paddingHorizontal: 10,
        },
        countBadge: {
            position: "absolute",
            top: -10,
            right: -10,
            backgroundColor: "red",
            borderRadius: 10,
            padding: 5,
            color: "#fff",
            fontSize: isMobile ? 12 : 18,
        },
        reminderButton: {
            backgroundColor: "#007bff",
            padding: isMobile ? 8 : 12,
            borderRadius: 8,
            marginTop: 10,
            alignSelf: "center",
        },
        reminderButtonText: {
            color: "#fff",
            fontSize: isMobile ? 14 : 16,
            fontWeight: "bold",
            textAlign: "center",
        },
        modalOverlay: {
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            justifyContent: "center",
            alignItems: "center",
        },
        modalContent: {
            backgroundColor: "white",
            borderRadius: 15,
            padding: 20,
            width: isMobile ? "90%" : 400,
            maxHeight: "80%",
        },
        modalTitle: {
            fontSize: 20,
            fontWeight: "bold",
            marginBottom: 15,
            textAlign: "center",
            color: "#333",
        },
        checkboxContainer: {
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 10,
            padding: 8,
        },
        checkbox: {
            width: 20,
            height: 20,
            borderWidth: 2,
            borderColor: "#007bff",
            borderRadius: 4,
            marginRight: 10,
            justifyContent: "center",
            alignItems: "center",
        },
        checkboxChecked: {
            backgroundColor: "#007bff",
        },
        checkboxText: {
            fontSize: 16,
            color: "#333",
            flex: 1,
        },
        modalButtons: {
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: 20,
        },
        modalButton: {
            flex: 1,
            padding: 12,
            borderRadius: 8,
            alignItems: "center",
            marginHorizontal: 5,
        },
        saveButton: {
            backgroundColor: "#28a745",
        },
        cancelButton: {
            backgroundColor: "#6c757d",
        },
        modalButtonText: {
            color: "white",
            fontWeight: "bold",
        },
        eventDetailsContainer: {
            marginTop: 15,
            padding: 12,
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#dee2e6',
        },
        eventDetailsTitle: {
            fontSize: 18,
            fontWeight: 'bold',
            color: '#495057',
            marginBottom: 8,
            textAlign: 'center',
        },
        eventDetailRow: {
            marginBottom: 4,
        },
        eventDetailLabel: {
            fontWeight: 'bold',
            color: '#6c757d',
            fontSize: 15,
        },
        eventDetailValue: {
            color: '#495057',
            fontSize: 16,
        },
        eventFormInput: {
            borderWidth: 1,
            borderColor: '#ced4da',
            borderRadius: 4,
            padding: 10,
            marginBottom: 10,
            backgroundColor: 'white',
            fontSize: 16,
        },
        eventFormLabel: {
            fontWeight: 'bold',
            color: '#495057',
            marginBottom: 5,
            fontSize: 16,
        },
        eventButton: {
            backgroundColor: "#17a2b8",
            padding: isMobile ? 8 : 12,
            borderRadius: 8,
            marginTop: 10,
            alignSelf: "center",
        },
        eventButtonText: {
            color: "#fff",
            fontSize: isMobile ? 14 : 16,
            fontWeight: "bold",
            textAlign: "center",
        },
    });

    return (
        <View style={{ padding: 10 }}>
            <View style={styles.container}>
                <TouchableOpacity
                    onPress={() => handleRSVP("Going")}
                    style={[styles.button, rsvp === "Going" && { backgroundColor: "green" }]}
                >
                    <Text style={styles.text}>I'll be there</Text>
                    <Text style={styles.countBadge}>{counts.Going}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    onPress={() => handleRSVP("Maybe")}
                    style={[styles.button, rsvp === "Maybe" && { backgroundColor: "beige" }]}
                >
                    <Text style={styles.text}>Maybe</Text>
                    <Text style={styles.countBadge}>{counts.Maybe}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    onPress={() => handleRSVP("Decline")}
                    style={[styles.button, rsvp === "Decline" && { backgroundColor: "red" }]}
                >
                    <Text style={styles.text}>Decline</Text>
                    <Text style={styles.countBadge}>{counts.Decline}</Text>
                </TouchableOpacity>
            </View>
            
            {/* Event Details Display for All Users */}
            {hasEventDetails && (
                <View style={styles.eventDetailsContainer}>
                    <Text style={styles.eventDetailsTitle}>🎓 Event Details</Text>
                    {eventDetails.title && (
                        <View style={styles.eventDetailRow}>
                            <Text style={styles.eventDetailLabel}>Event: </Text>
                            <Text style={styles.eventDetailValue}>{eventDetails.title}</Text>
                        </View>
                    )}
                    {eventDetails.date && (
                        <View style={styles.eventDetailRow}>
                            <Text style={styles.eventDetailLabel}>Date & Time: </Text>
                            <Text style={styles.eventDetailValue}>{new Date(eventDetails.date).toLocaleDateString('en-US', { 
                                weekday: 'long',
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                            })}</Text>
                        </View>
                    )}
                    {eventDetails.location && (
                        <View style={styles.eventDetailRow}>
                            <Text style={styles.eventDetailLabel}>Location: </Text>
                            <Text style={styles.eventDetailValue}>{eventDetails.location}</Text>
                        </View>
                    )}
                    {eventDetails.description && (
                        <View style={styles.eventDetailRow}>
                            <Text style={styles.eventDetailLabel}>Description: </Text>
                            <Text style={styles.eventDetailValue}>{eventDetails.description}</Text>
                        </View>
                    )}
                </View>
            )}

            {/* Owner Controls - Event Details and Reminder Settings */}
            {isOwner && (
                <View style={{ 
                    flexDirection: isMobile ? 'column' : 'row', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    gap: 10, 
                    marginTop: 15,
                    paddingVertical: 10,
                    backgroundColor: '#f0f8ff',
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: '#007bff'
                }}>
                    <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: isMobile ? 5 : 0 }}>Page Owner Controls:</Text>
                    <TouchableOpacity
                        style={[styles.eventButton, { marginHorizontal: 5 }]}
                        onPress={() => setShowEventDetails(true)}
                    >
                        <Text style={styles.eventButtonText}>
                            {hasEventDetails ? 'Edit Event Details' : 'Add Event Details'}
                        </Text>
                    </TouchableOpacity>
                    
                    {showRemindersFeature && (
                        <TouchableOpacity
                            style={[styles.reminderButton, { marginHorizontal: 5 }]}
                            onPress={() => setShowReminderSettings(true)}
                        >
                            <Text style={styles.reminderButtonText}>
                                Reminder Settings {reminderSettings.enabled ? '(Active)' : ''}
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}

            {/* Event Details Modal */}
            <Modal
                visible={showEventDetails}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowEventDetails(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>Event Details</Text>
                        
                        <Text style={styles.eventFormLabel}>Event Title</Text>
                        <TextInput
                            style={styles.eventFormInput}
                            value={eventDetails.title}
                            onChangeText={(text) => setEventDetails(prev => ({ ...prev, title: text }))}
                            placeholder="e.g., John's Graduation Ceremony"
                        />
                        
                        <Text style={styles.eventFormLabel}>Date & Time</Text>
                        <Text style={{ fontSize: 14, color: '#6c757d', marginBottom: 5 }}>
                            Format: YYYY-MM-DDTHH:MM (e.g., 2026-05-15T14:00 for May 15, 2026 at 2:00 PM)
                        </Text>
                        <TextInput
                            style={styles.eventFormInput}
                            value={eventDetails.date}
                            onChangeText={(text) => setEventDetails(prev => ({ ...prev, date: text }))}
                            placeholder="2026-05-15T14:00"
                        />
                        
                        <Text style={styles.eventFormLabel}>Location</Text>
                        <TextInput
                            style={styles.eventFormInput}
                            value={eventDetails.location}
                            onChangeText={(text) => setEventDetails(prev => ({ ...prev, location: text }))}
                            placeholder="e.g., University Auditorium"
                        />
                        
                        <Text style={styles.eventFormLabel}>Description</Text>
                        <TextInput
                            style={[styles.eventFormInput, { height: 80, textAlignVertical: 'top' }]}
                            value={eventDetails.description}
                            onChangeText={(text) => setEventDetails(prev => ({ ...prev, description: text }))}
                            placeholder="Additional details about the event..."
                            multiline={true}
                        />

                        <View style={styles.modalButtons}>
                            <TouchableOpacity
                                style={[styles.modalButton, styles.cancelButton]}
                                onPress={() => setShowEventDetails(false)}
                            >
                                <Text style={styles.modalButtonText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.modalButton, styles.saveButton]}
                                onPress={saveEventDetails}
                            >
                                <Text style={styles.modalButtonText}>Save Details</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* Reminder Settings Modal - controlled by feature flag */}
            {showRemindersFeature && (
            <Modal
                visible={showReminderSettings}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowReminderSettings(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>Event Reminder Settings</Text>
                        
                        <Text style={{ marginBottom: 15, color: '#666', textAlign: 'center' }}>
                            Select when to send reminders to RSVP'd guests:
                        </Text>

                        {reminderOptions.map((option) => {
                            const isSelected = reminderSettings.reminderTimes.includes('day_of') && 
                                             reminderSettings.reminderTimes.includes(option.value);
                            
                            return (
                                <TouchableOpacity
                                    key={option.value}
                                    style={styles.checkboxContainer}
                                    onPress={() => {
                                        const newReminderTimes = isSelected 
                                            ? reminderSettings.reminderTimes.filter(t => 
                                                t !== 'day_of' && t !== option.value
                                              )
                                            : [...reminderSettings.reminderTimes.filter(t => 
                                                t !== 'day_of' && !t.startsWith('days_before_')
                                              ), 'day_of', option.value];
                                        
                                        setReminderSettings(prev => ({
                                            ...prev,
                                            reminderTimes: newReminderTimes,
                                            enabled: newReminderTimes.length > 0
                                        }));
                                    }}
                                >
                                    <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
                                        {isSelected && (
                                            <Text style={{ color: 'white', fontWeight: 'bold' }}>✓</Text>
                                        )}
                                    </View>
                                    <Text style={styles.checkboxText}>{option.label}</Text>
                                </TouchableOpacity>
                            );
                        })}

                        {isAdminUser && (
                            <View style={{ marginTop: 16, borderTopWidth: 1, borderTopColor: '#e0e0e0', paddingTop: 12 }}>
                                <Text style={{ fontSize: 12, fontWeight: '700', color: '#b45309', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Admin Testing</Text>
                                {testReminderOptions.map((option) => (
                                    <TouchableOpacity
                                        key={option.value}
                                        style={[styles.checkboxContainer, { backgroundColor: '#fffbeb', borderRadius: 8, padding: 8, marginBottom: 4 }]}
                                        onPress={() => scheduleTestReminder(option.testDelay)}
                                    >
                                        <Text style={{ fontSize: 13, color: '#b45309', fontWeight: '600' }}>{option.label}</Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        )}

                        <View style={styles.modalButtons}>
                            <TouchableOpacity
                                style={[styles.modalButton, styles.cancelButton]}
                                onPress={() => setShowReminderSettings(false)}
                            >
                                <Text style={styles.modalButtonText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.modalButton, styles.saveButton]}
                                onPress={saveReminderSettings}
                            >
                                <Text style={styles.modalButtonText}>Save Settings</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
            )}
        </View>
    );
};

export default RSVP;